/**
 * Portail public de la carte (/c/:slug) — sans authentification.
 * Lien unique : une carte par défaut (choisie dans Éditions) + les cartes cochées affichées
 * en onglets. S'il n'y a qu'une carte (aucune cochée), elle s'affiche directement sans onglet.
 * Chaque onglet est une édition (modèle × langue) : la langue est portée par l'onglet, il n'y a
 * donc pas de sélecteur de langue séparé. Champ « Filtrer » instantané. Toujours à jour du stock.
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { carteApi } from '../../services/api';
import { Spinner } from '../../components/ui/kit';
import CardPreview from './CardPreview';

export default function PublicCarte() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const m = params.get('m') || undefined;            // modèle imposé (lien d'édition dédié)
  const urlLang = params.get('lang') || undefined;   // langue imposée par le lien dédié
  const [structure, setStructure] = useState(undefined); // {tabs:[{template_id,lang,name}], default_*}
  const [activeKey, setActiveKey] = useState(null);  // onglet sélectionné : `template_id|lang`
  const [content, setContent] = useState(undefined);
  const [filter, setFilter] = useState('');
  const [count, setCount] = useState([0, 0]);

  // Lien unique (sans ?m=) : récupérer les onglets du portail. Lien dédié : pas d'onglets.
  useEffect(() => {
    if (m) { setStructure(null); return; }
    carteApi.portalStructure(slug).then(setStructure).catch(() => setStructure(null));
  }, [slug, m]);

  const tabs = (!m && structure?.tabs) || [];
  const defaultKey = structure ? `${structure.default_template_id}|${structure.default_lang}` : null;
  const currentKey = m ? null : (activeKey || defaultKey);
  const [tabTid, tabLang] = currentKey ? currentKey.split('|') : [undefined, undefined];

  // Carte servie : lien dédié (m + langue du lien) ou onglet courant (modèle + langue de l'onglet).
  const effectiveM = m || tabTid || undefined;
  const effectiveLang = m ? urlLang : tabLang;
  const ready = m ? true : structure !== undefined;

  useEffect(() => {
    if (!ready || (!m && !structure)) return;
    setContent(undefined);
    carteApi.publicCarte(slug, effectiveLang, effectiveM).then(setContent).catch(() => setContent(null));
  }, [slug, effectiveM, effectiveLang, ready]);

  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-paper"><Spinner /></div>;
  if (!m && structure === null) return <div className="min-h-screen flex items-center justify-center bg-paper text-center p-6"><p className="font-title text-2xl">Carte indisponible</p></div>;
  if (content === undefined) return <div className="min-h-screen flex items-center justify-center bg-paper"><Spinner /></div>;
  if (content === null) return <div className="min-h-screen flex items-center justify-center bg-paper text-center p-6"><p className="font-title text-2xl">Carte indisponible</p></div>;

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 bg-paper/95 backdrop-blur hair-b z-10">
        <div className="max-w-[720px] mx-auto px-4 py-3">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <span className="font-title text-2xl">{content.establishment}</span>
            {tabs.length > 1 && (
              <div className="flex flex-wrap gap-1.5 justify-end" data-testid="portal-tabs">
                {tabs.map((t) => {
                  const key = `${t.template_id}|${t.lang}`;
                  const active = currentKey === key;
                  return (
                    <button key={key} onClick={() => setActiveKey(key)}
                      data-testid={`portal-tab-btn-${key}`}
                      className={`px-3 h-8 rounded-full text-[13px] border transition-colors ${active ? 'bg-gold100 text-gold800 border-gold' : 'text-n600 border-hair hover:bg-surface'}`}>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="portal-filter"
              placeholder={content.labels?.filter || 'Filtrer…'} className="vd-input h-9 flex-1 text-[14px]" />
            {filter && <span className="text-[12px] text-n600 nums" data-testid="portal-count">{count[0]} / {count[1]}</span>}
          </div>
        </div>
      </header>
      <main className="max-w-[720px] mx-auto px-4 py-6" data-testid="public-carte">
        <CardPreview content={content} filter={filter} onCount={(s, t) => setCount((p) => (p[0] === s && p[1] === t ? p : [s, t]))} />
        {(content.contact?.website || content.contact?.phone || content.contact?.email) && (
          <footer className="mt-8 pt-5 hair-t text-center text-[13px] text-n600 flex flex-wrap items-center justify-center gap-x-4 gap-y-1" data-testid="portal-contact">
            {content.contact.website && (
              <a href={/^https?:\/\//.test(content.contact.website) ? content.contact.website : `https://${content.contact.website}`} target="_blank" rel="noreferrer" className="vd-link" data-testid="portal-contact-website">{content.contact.website.replace(/^https?:\/\//, '')}</a>
            )}
            {content.contact.phone && <a href={`tel:${content.contact.phone.replace(/\s/g, '')}`} className="vd-link" data-testid="portal-contact-phone">{content.contact.phone}</a>}
            {content.contact.email && <a href={`mailto:${content.contact.email}`} className="vd-link" data-testid="portal-contact-email">{content.contact.email}</a>}
          </footer>
        )}
        <footer className="mt-10 pt-5 hair-t flex flex-col items-center gap-2 text-center" data-testid="portal-powered">
          <a href="https://cavorama.fr/" target="_blank" rel="noreferrer" aria-label="Cavorama.fr">
            <img src="/cavorama-logo.png" alt="Cavorama.fr" className="h-7 w-auto opacity-90 hover:opacity-100 transition-opacity" />
          </a>
          <a href="https://cavorama.fr/" target="_blank" rel="noreferrer" className="text-[12.5px] text-n500 hover:text-ink transition-colors max-w-[420px]" data-testid="portal-powered-text">
            Carte des vins publiée avec l'app Cavorama.fr — Essayez-le gratuitement !
          </a>
        </footer>
      </main>
    </div>
  );
}
